/**
 * What auto-mix is allowed to choose between.
 *
 * Pure and in its own module so it can be tested without an AudioContext, and
 * so there is exactly one definition of "selectable". The controller filters
 * its library through this and reports `poolSize` from the result, which means
 * the number shown to the operator and the number the selector holds cannot
 * drift apart — they are the same array.
 */

export interface PoolTrack {
  id: number;
  missing: boolean;
  excluded: boolean;
  analysis_state: string | null;
}

/**
 * A track is selectable when it is present, not marked junk, and analysed.
 *
 * Analysis is required rather than preferred (X10): without peaks there are no
 * tails to trim and no beats to align to, so an unanalysed track cannot be
 * transitioned out of — auto-mix would arrive at its end with nothing planned.
 */
export function isSelectable(track: PoolTrack): boolean {
  return !track.missing && !track.excluded && track.analysis_state === "ok";
}

export function selectablePool<T extends PoolTrack>(tracks: readonly T[]): T[] {
  return tracks.filter(isSelectable);
}

/** Below this, there is nothing to transition *to*. */
export const MIN_POOL = 2;

/**
 * Why auto-mix cannot arm, or null when it can.
 *
 * Returns a sentence rather than a boolean because the interesting case is a
 * tag whose visible track count looks fine but whose *analysed* count does not.
 * "Nothing happened" is the worst possible answer there.
 */
export function poolRefusal(poolSize: number, tag: string | null): string | null {
  if (poolSize >= MIN_POOL) return null;
  const need = `auto-mix needs ${MIN_POOL}`;
  return tag
    ? `Tag "${tag}" has ${poolSize} analysed track(s); ${need}.`
    : `Only ${poolSize} analysed track(s) available; ${need}.`;
}
